import { useState, useEffect } from 'react';
import './styles/global.css';

const MCP = 'http://localhost:3008';

const FILES = [
  { name: 'index.tsx', lang: 'typescript' },
  { name: 'api.ts', lang: 'typescript' },
  { name: 'utils.py', lang: 'python' },
  { name: 'README.md', lang: 'markdown' },
];

const SAMPLE_CODE = `import { useState, useEffect } from 'react';

export function useLocalStorage<T>(key: string, initial: T): [T, (v: T) => void] {
  const [value, setValue] = useState<T>(() => {
    const stored = window.localStorage.getItem(key);
    return stored ? JSON.parse(stored) : initial;
  });

  useEffect(() => {
    window.localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);

  return [value, setValue];
}`;

function App() {
  const [active, setActive] = useState('index.tsx');
  const [chatOpen, setChatOpen] = useState(true);
  const [messages, setMessages] = useState<{ role: 'user' | 'assistant'; content: string }[]>([]);
  const [input, setInput] = useState('');

  async function send() {
    const text = input.trim();
    if (!text) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }]);
    try {
      const r = await fetch(`${MCP}/mcp/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer nb_prod_atlas_enterprise_a1b2c3d4e5f6g7h8i9j0' },
        body: JSON.stringify({ tool: 'aether_chat', arguments: { message: text, model: 'aether-orion' } }),
      });
      const d = await r.json();
      const reply = d.response || `[Helix AI] Received: "${text.slice(0, 80)}". Set OPENAI/ANTHROPIC/AETHER keys for real AI.`;
      setMessages((m) => [...m, { role: 'assistant', content: reply }]);
    } catch {
      setMessages((m) => [...m, { role: 'assistant', content: '⚠ Could not reach MCP gateway.' }]);
    }
  }

  return (
    <div className={`helix-shell ${chatOpen ? '' : 'no-chat'}`}>
      <aside className="helix-sidebar">
        <div className="helix-brand">
          <div className="helix-brand-mark">H</div>
          <span>Helix IDE</span>
        </div>
        <div className="helix-files">
          <div style={{ padding: '4px 12px', fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>EXPLORER</div>
          {FILES.map((f) => (
            <div key={f.name} className={`helix-file ${active === f.name ? 'active' : ''}`} onClick={() => setActive(f.name)}>
              <span>📄</span>
              <span>{f.name}</span>
            </div>
          ))}
        </div>
      </aside>
      <main className="helix-main">
        <div className="helix-tabs">
          {FILES.map((f) => (
            <div key={f.name} className={`helix-tab ${active === f.name ? 'active' : ''}`} onClick={() => setActive(f.name)}>
              <span>📄</span>
              <span>{f.name}</span>
            </div>
          ))}
        </div>
        <div className="helix-editor">
          <div className="helix-gutter">
            {Array.from({ length: 18 }, (_, i) => <div key={i}>{i + 1}</div>)}
          </div>
          <div className="helix-code">
            <span className="kw">import</span> {'{'} <span className="kw">useState</span>, <span className="kw">useEffect</span> {'}'} <span className="kw">from</span> <span className="str">'react'</span>;{'\n\n'}
            <span className="kw">export function</span> <span style={{ color: '#d2a8ff' }}>useLocalStorage</span>{'<'}<span className="typ">T</span>{'>'}(<span style={{ color: '#ffa657' }}>key</span>: <span className="typ">string</span>, <span style={{ color: '#ffa657' }}>initial</span>: <span className="typ">T</span>): {'['}<span className="typ">T</span>, (<span style={{ color: '#ffa657' }}>v</span>: <span className="typ">T</span>) { '=>'} <span className="kw">void</span>{']'} {'{'}{'\n'}
            {'  '}<span className="kw">const</span> [<span style={{ color: '#ffa657' }}>value</span>, <span style={{ color: '#ffa657' }}>setValue</span>] = <span className="fn">useState</span>{'<'}<span className="typ">T</span>{'>'}(() { '=>'} {'{'}{'\n'}
            {'    '}<span className="kw">const</span> <span style={{ color: '#ffa657' }}>stored</span> = <span className="var">window</span>.<span className="var">localStorage</span>.<span className="fn">getItem</span>(<span style={{ color: '#ffa657' }}>key</span>);{'\n'}
            {'    '}<span className="kw">return</span> <span style={{ color: '#ffa657' }}>stored</span> ? <span className="fn">JSON.parse</span>(<span style={{ color: '#ffa657' }}>stored</span>) : <span style={{ color: '#ffa657' }}>initial</span>;{'\n'}
            {'  '}{'}'});{'\n\n'}
            {'  '}<span className="fn">useEffect</span>(() { '=>'} {'{'}{'\n'}
            {'    '}<span className="var">window</span>.<span className="var">localStorage</span>.<span className="fn">setItem</span>(<span style={{ color: '#ffa657' }}>key</span>, <span className="fn">JSON.stringify</span>(<span style={{ color: '#ffa657' }}>value</span>));{'\n'}
            {'  '}{'}'}, [<span style={{ color: '#ffa657' }}>key</span>, <span style={{ color: '#ffa657' }}>value</span>]);{'\n\n'}
            {'  '}<span className="kw">return</span> [<span style={{ color: '#ffa657' }}>value</span>, <span style={{ color: '#ffa657' }}>setValue</span>];{'\n'}
            {'}'}
          </div>
        </div>
      </main>
      {chatOpen && (
        <aside className="helix-chat">
          <div className="helix-chat-header">
            <div className="helix-chat-title">✦ Helix AI</div>
            <button onClick={() => setChatOpen(false)} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>×</button>
          </div>
          <div className="helix-chat-msgs">
            {messages.length === 0 ? (
              <div style={{ margin: 'auto', textAlign: 'center', color: 'var(--text-muted)', padding: 20 }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>✦</div>
                <div style={{ fontWeight: 600, color: 'var(--text)' }}>Helix AI</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>Your coding assistant</div>
              </div>
            ) : messages.map((m, i) => (
              <div key={i} className={`helix-msg ${m.role}`}>{m.content}</div>
            ))}
          </div>
          <div className="helix-input-wrap">
            <textarea className="helix-input" placeholder="Ask Helix AI…" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); } }} />
            <button onClick={send} style={{ marginTop: 8, width: '100%', background: 'var(--accent-2)', color: 'var(--bg)', border: 'none', padding: '8px', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}>Send (⌘+Enter)</button>
          </div>
        </aside>
      )}
    </div>
  );
}

export default App;
